import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MessageCircle,
  AlertCircle,
  Sparkles,
} from "lucide-react";
import { trackEvent } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITIONS = [
  "Type 2 Diabetes / Prediabetes",
  "Hypertension (high BP)",
  "High cholesterol",
  "PCOS / PCOD",
  "Fatty liver",
  "Thyroid disorder",
  "Joint pain from weight",
  "None of the above",
];

const classify = (bmi, hasCondition) => {
  if (!isFinite(bmi) || bmi <= 0) return null;
  // Mounjaro / GLP-1 eligibility follows US guidance adapted for Indian BMI cut-offs
  if (bmi >= 30) return "strong";
  if (bmi >= 27 && hasCondition) return "strong";
  if (bmi >= 27) return "likely";
  if (bmi >= 23 && hasCondition) return "review";
  return "lifestyle";
};

export const BMIQuiz = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    height_cm: "",
    weight_kg: "",
    conditions: [],
    name: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const bmi = useMemo(() => {
    const h = parseFloat(form.height_cm) / 100;
    const w = parseFloat(form.weight_kg);
    if (!h || !w) return 0;
    return +(w / (h * h)).toFixed(1);
  }, [form.height_cm, form.weight_kg]);

  const hasCondition =
    form.conditions.length > 0 &&
    !(form.conditions.length === 1 && form.conditions[0] === "None of the above");
  const verdict = classify(bmi, hasCondition);

  const reset = () => {
    setStep(0);
    setForm({ height_cm: "", weight_kg: "", conditions: [], name: "", phone: "" });
    setSubmitted(false);
    setLoading(false);
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(reset, 250);
  };

  const toggleCond = (c) => {
    setForm((f) => {
      const exists = f.conditions.includes(c);
      if (c === "None of the above") {
        return { ...f, conditions: exists ? [] : ["None of the above"] };
      }
      const rest = f.conditions.filter((x) => x !== "None of the above");
      return {
        ...f,
        conditions: exists ? rest.filter((x) => x !== c) : [...rest, c],
      };
    });
  };

  const next = () => {
    if (step === 0 && (!form.height_cm || !form.weight_kg)) {
      toast.error("Please enter height and weight.");
      return;
    }
    if (step === 0 && bmi < 14) {
      toast.error("Please check the values — BMI seems too low.");
      return;
    }
    if (step === 1 && form.conditions.length === 0) {
      toast.error("Select at least one option (or 'None of the above').");
      return;
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, {
        name: form.name,
        phone: form.phone,
        weight_goal: `BMI ${bmi} · ${form.height_cm}cm / ${form.weight_kg}kg`,
        message:
          `BMI Quiz · verdict=${verdict} · conditions=` +
          (form.conditions.join(", ") || "none"),
      });
      setSubmitted(true);
      trackEvent("quiz_completed", { bmi, verdict, conditions: form.conditions.length });
      toast.success("Thank you. Dr. Shalini's team will contact you shortly.");
    } catch (e) {
      console.error(e);
      toast.error("Could not submit. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  // ----- Step content -----
  const stepNodes = [
    // 0 — height & weight
    <div key="0" className="space-y-5">
      <div>
        <Label htmlFor="h" className="font-micro">Your height (cm)</Label>
        <Input
          id="h" type="number" inputMode="numeric" data-testid="quiz-height"
          value={form.height_cm}
          onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
          placeholder="e.g. 165"
          className="mt-2 h-12 rounded-xl"
        />
      </div>
      <div>
        <Label htmlFor="w" className="font-micro">Your weight (kg)</Label>
        <Input
          id="w" type="number" inputMode="numeric" data-testid="quiz-weight"
          value={form.weight_kg}
          onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
          placeholder="e.g. 82"
          className="mt-2 h-12 rounded-xl"
        />
      </div>
      {bmi > 0 && (
        <div className="rounded-2xl bg-[#FAFAF7] border border-[#EAE7E1] p-4 flex items-center justify-between">
          <span className="font-micro">Your BMI</span>
          <span className="font-display text-3xl text-[#242220]">{bmi}</span>
        </div>
      )}
    </div>,

    // 1 — conditions
    <div key="1" className="space-y-3">
      <p className="text-sm text-[#38312C]">
        Select any that apply (we use this to check eligibility and safety).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {CONDITIONS.map((c) => {
          const active = form.conditions.includes(c);
          return (
            <button
              key={c}
              type="button"
              data-testid={`quiz-cond-${c.toLowerCase().split(" ")[0]}`}
              onClick={() => toggleCond(c)}
              className={`text-left text-sm rounded-2xl border px-4 py-3 transition-all ${
                active
                  ? "bg-[#779D7C] border-[#779D7C] text-white"
                  : "bg-white border-[#EAE7E1] text-[#242220] hover:border-[#779D7C]"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                {active && <CheckCircle2 className="h-4 w-4" />}
                {c}
              </span>
            </button>
          );
        })}
      </div>
    </div>,

    // 2 — verdict + lead capture
    <div key="2" className="space-y-5">
      {verdict === "strong" && (
        <div className="rounded-2xl bg-[#EEF5EF] border border-[#779D7C]/30 p-5">
          <p className="font-micro text-[#2E936F]">Strong candidate</p>
          <p className="font-display text-2xl text-[#242220] mt-1">
            You're likely eligible for Mounjaro / GLP‑1 therapy.
          </p>
          <p className="text-sm text-[#38312C] mt-2">
            With BMI <b>{bmi}</b>{hasCondition ? " and your current conditions" : ""}, Dr. Shalini
            can usually offer a supervised programme after a short eligibility call.
          </p>
        </div>
      )}
      {verdict === "likely" && (
        <div className="rounded-2xl bg-[#FCF3EC] border border-[#EB8771]/30 p-5">
          <p className="font-micro text-[#B84E38]">Likely candidate</p>
          <p className="font-display text-2xl text-[#242220] mt-1">
            You may qualify — let's confirm.
          </p>
          <p className="text-sm text-[#38312C] mt-2">
            Your BMI ({bmi}) is in the range where GLP‑1 therapy can help when combined with
            lifestyle change. A quick consult will confirm.
          </p>
        </div>
      )}
      {verdict === "review" && (
        <div className="rounded-2xl bg-[#FAFAF7] border border-[#EAE7E1] p-5">
          <p className="font-micro">Clinical review recommended</p>
          <p className="font-display text-2xl text-[#242220] mt-1">
            Dr. Shalini will review your case carefully.
          </p>
          <p className="text-sm text-[#38312C] mt-2">
            With BMI {bmi} and existing conditions, we tailor therapy — sometimes GLP‑1, sometimes
            lifestyle first. Book a call to decide together.
          </p>
        </div>
      )}
      {verdict === "lifestyle" && (
        <div className="rounded-2xl bg-[#FAFAF7] border border-[#EAE7E1] p-5">
          <AlertCircle className="h-5 w-5 text-[#B84E38]" />
          <p className="font-display text-2xl text-[#242220] mt-3">
            Medication likely isn't the right first step.
          </p>
          <p className="text-sm text-[#38312C] mt-2">
            Your BMI ({bmi}) suggests a nutrition & lifestyle programme will serve you better.
            Dr. Shalini still offers structured metabolic coaching — happy to guide.
          </p>
        </div>
      )}

      <div className="pt-2 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="qn" className="font-micro">Your name</Label>
            <Input
              id="qn" data-testid="quiz-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Anitha Reddy"
              className="mt-2 h-12 rounded-xl"
            />
          </div>
          <div>
            <Label htmlFor="qp" className="font-micro">Phone</Label>
            <Input
              id="qp" data-testid="quiz-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="+91 98765 43210"
              className="mt-2 h-12 rounded-xl"
            />
          </div>
        </div>
        <p className="text-xs text-[#6B6660]">
          <b>Prefer an in‑clinic visit?</b> First visits are best done in person so Dr. Shalini can run a
          BIA body composition scan and personally teach you how to take the injection. Walk in Mon–Sat,
          11 AM – 3:30 PM, or call/WhatsApp us directly.<br />
          Fill the form below only if you need an <b>online video consultation</b>. 100% confidential.
        </p>
      </div>
    </div>,
  ];

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="bmi-quiz-dialog"
        className="max-w-xl rounded-[28px] border-[#EAE7E1] p-0 overflow-hidden"
      >
        {/* Header */}
        <div className="px-7 pt-7">
          <DialogHeader>
            <span className="font-micro inline-flex items-center gap-2 text-[#2E936F]">
              <Sparkles className="h-3.5 w-3.5" /> 30‑second eligibility quiz
            </span>
            <DialogTitle className="font-display text-3xl text-[#242220] mt-2 leading-tight">
              {submitted
                ? "You're all set."
                : step === 2
                ? "Here's your result"
                : "Am I eligible for Mounjaro?"}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#38312C]">
              {submitted
                ? "Dr. Shalini's team will reach out on WhatsApp within one working day."
                : "Answer a couple of questions so Dr. Shalini's team can guide you."}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Progress */}
        {!submitted && (
          <div className="px-7 mt-4 flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-[#EB8771]" : "bg-[#EAE7E1]"
                }`}
              />
            ))}
          </div>
        )}

        {/* Body */}
        <div className="px-7 py-6">
          {submitted ? (
            <div className="text-center py-6">
              <div className="h-14 w-14 rounded-full bg-[#EEF5EF] grid place-items-center mx-auto">
                <CheckCircle2 className="h-7 w-7 text-[#2E936F]" strokeWidth={1.7} />
              </div>
              <p className="font-display text-2xl mt-5 text-[#242220]">
                Request received · thank you, {form.name.split(" ")[0]}.
              </p>
              <p className="text-sm text-[#38312C] mt-2 max-w-sm mx-auto">
                Meanwhile, feel free to WhatsApp us directly — we usually reply within the hour.
              </p>
              <a
                href="https://wa.me/919133552279?text=Hi%20Dr.%20Shalini%2C%20I%20just%20completed%20the%20eligibility%20quiz."
                target="_blank"
                rel="noreferrer"
                data-testid="quiz-whatsapp"
                onClick={() => trackEvent("whatsapp_clicked", { location: "quiz_success" })}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white px-6 py-3 text-sm font-medium transition-all hover:scale-[1.03]"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp Dr. Shalini
              </a>
            </div>
          ) : (
            stepNodes[step]
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="px-7 pb-7 flex items-center justify-between gap-3 border-t border-[#EAE7E1] pt-5">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="quiz-back"
              className="inline-flex items-center gap-2 text-sm text-[#38312C] disabled:opacity-40"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            {step < 2 ? (
              <button
                type="button"
                onClick={next}
                data-testid="quiz-next"
                className="inline-flex items-center gap-2 rounded-full bg-[#EB8771] hover:bg-[#E2725C] text-white px-6 py-3 text-sm font-medium transition-all hover:scale-[1.03]"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={loading}
                data-testid="quiz-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#EB8771] hover:bg-[#E2725C] text-white px-6 py-3 text-sm font-medium transition-all hover:scale-[1.03] disabled:opacity-60"
              >
                {loading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : "See my result & book"}
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
